/* src/components/suggestions/SuggestionCard.js */

import React, { useEffect, useRef, useState } from "react";
import { FiEye, FiEyeOff, FiCheckCircle, FiRotateCcw, FiEdit2, FiTrash2 } from "react-icons/fi";

import { urgencyMeta, statusMeta } from "./suggestionsLogic";

import "./SuggestionCard.css";

function formatWhen(ms) {
  if (!ms) return "Just now";

  const diff = Date.now() - ms;
  if (diff < 60 * 1000) return "Just now";
  if (diff < 60 * 60 * 1000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 24 * 60 * 60 * 1000) return `${Math.floor(diff / 3600000)}h ago`;
  if (diff < 7 * 24 * 60 * 60 * 1000) return `${Math.floor(diff / 86400000)}d ago`;

  return new Date(ms).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatFull(ms) {
  if (!ms) return "";
  return new Date(ms).toLocaleString();
}

// Safety net only: truncation is normally detected by measuring the clamped
// element (which is width-aware — the 4 CSS-clamped lines hold ~500 chars on a
// desktop card but only ~180 on a phone). This threshold just guarantees the
// toggle still appears if that measurement is ever unavailable, so it is
// deliberately high enough to never fire on a card where the text already fits.
const COLLAPSE_CHAR_THRESHOLD = 600;

export default function SuggestionCard({
  suggestion,
  isAdmin,
  currentUid,
  onEdit,
  onMarkRead,
  onMarkUnread,
  onMarkCompleted,
  onMarkIncomplete,
  onDelete,
}) {
  const [expanded, setExpanded] = useState(false);
  const [clamped, setClamped] = useState(false);
  const descRef = useRef(null);

  const urgency = urgencyMeta(suggestion.urgency);
  const status = statusMeta(suggestion.status);
  const statusValue = status.value;

  // Admins may edit anything; a tutor only their own submission. (Tutors are
  // only ever sent their own docs, so this is a belt-and-braces check that
  // mirrors firestore.rules.)
  const canEdit = isAdmin || (!!currentUid && suggestion.createdBy === currentUid);

  // Whether the author had already read their suggestion back — used to decide
  // what "Mark as incomplete" should revert to.
  const wasRead = !!(suggestion.readAt || suggestion.readBy);

  const description = suggestion.description || "";

  // Detect whether the (clamped-until-expanded) description overflows so the
  // "Show more" toggle appears whenever there is genuinely more to see.
  useEffect(() => {
    const el = descRef.current;
    if (!el || expanded) return;
    setClamped(el.scrollHeight > el.clientHeight + 1);
  }, [description, expanded]);

  const canExpand = expanded || clamped || description.length > COLLAPSE_CHAR_THRESHOLD;

  // The fade is only a truncation cue, so it must never appear unless the text
  // is actually cut off — i.e. unless the "Show more" toggle is rendered too.
  const showFade = canExpand && !expanded;

  const readAtMs = suggestion.readAt?.toMillis?.() ?? null;
  const completedAtMs = suggestion.completedAt?.toMillis?.() ?? null;

  const showReadAudit = statusValue === "read" || statusValue === "completed";
  const showCompletedAudit = statusValue === "completed";

  return (
    <article className="sgCard">
      <div className="sgCardHead">
        <div className="sgCardHeadMain">
          <div className="sgCardTitleRow">
            <h3 className="sgCardTitle">{suggestion.title || "Untitled suggestion"}</h3>
            <span className={`sgBadge sgBadge--urgency-${urgency.value}`}>
              {urgency.label}
            </span>
            <span className={`sgBadge sgBadge--status-${statusValue}`}>
              {status.label}
            </span>
          </div>

          <div className="sgCardMeta">
            {isAdmin && suggestion.createdByName && (
              <>
                <span className="sgCardMetaAuthor">{suggestion.createdByName}</span>
                <span className="sgCardMetaDot">•</span>
              </>
            )}
            <span title={formatFull(suggestion.createdAtMs)}>
              {formatWhen(suggestion.createdAtMs)}
            </span>
            {suggestion.editedAtMs && (
              <>
                <span className="sgCardMetaDot">•</span>
                <span
                  className="sgCardMetaEdited"
                  title={`Edited${
                    suggestion.editedByName ? ` by ${suggestion.editedByName}` : ""
                  } · ${formatFull(suggestion.editedAtMs)}`}
                >
                  edited {formatWhen(suggestion.editedAtMs)}
                </span>
              </>
            )}
          </div>
        </div>

        {(isAdmin || canEdit) && (
          <div className="sgCardActions">
            {/* Status transitions are admin-only. */}
            {isAdmin && statusValue === "new" && (
              <button
                className="sgActionBtn"
                onClick={onMarkRead}
                title="Mark as read"
              >
                <FiEye size={15} />
                <span>Mark as read</span>
              </button>
            )}

            {isAdmin && statusValue === "read" && (
              <button
                className="sgActionBtn"
                onClick={onMarkUnread}
                title="Mark as unread"
              >
                <FiEyeOff size={15} />
                <span>Mark as unread</span>
              </button>
            )}

            {isAdmin && statusValue !== "completed" && (
              <button
                className="sgActionBtn sgActionBtn--primary"
                onClick={onMarkCompleted}
                title="Mark as completed"
              >
                <FiCheckCircle size={15} />
                <span>Mark as completed</span>
              </button>
            )}

            {isAdmin && statusValue === "completed" && (
              <button
                className="sgActionBtn"
                onClick={() => onMarkIncomplete(wasRead)}
                title="Mark as incomplete"
              >
                <FiRotateCcw size={15} />
                <span>Mark as incomplete</span>
              </button>
            )}

            {canEdit && (
              <button className="sgActionBtn" onClick={onEdit} title="Edit suggestion">
                <FiEdit2 size={15} />
                <span>Edit</span>
              </button>
            )}

            {/* Admins can delete anything; authors can retract their own. */}
            {canEdit && (
              <button
                className="sgIconBtn sgIconBtn--danger"
                onClick={onDelete}
                title="Delete suggestion"
                aria-label="Delete suggestion"
              >
                <FiTrash2 size={15} />
              </button>
            )}
          </div>
        )}
      </div>

      <div
        ref={descRef}
        className={[
          "sgCardDesc",
          showFade ? "sgCardDesc--faded" : "",
          expanded ? "sgCardDesc--expanded" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {description}
      </div>

      {canExpand && (
        <button
          className="sgCardMoreBtn"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      )}

      {(showReadAudit || showCompletedAudit) && (
        <div className="sgCardAudit">
          {showReadAudit && suggestion.readBy && (
            <span title={formatFull(readAtMs)}>
              Read by {suggestion.readBy}
              {readAtMs ? ` · ${formatWhen(readAtMs)}` : ""}
            </span>
          )}
          {showCompletedAudit && suggestion.completedBy && (
            <span title={formatFull(completedAtMs)}>
              Completed by {suggestion.completedBy}
              {completedAtMs ? ` · ${formatWhen(completedAtMs)}` : ""}
            </span>
          )}
        </div>
      )}
    </article>
  );
}
